import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, reactive, ref, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'

import { PolicySettingType, PolicyType } from '@/views/dashboard/_enum/policy-type'
import { StrategyListDataCommon, PolicyCommonService } from '../../policy/policy-common.service'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { TableOptColumn } from '@/components/cmTable/types'
import ItemGroup from '@/views/_components/item-group/item-group.vue'
import { GroupRefreshManage } from '@/services/group'
import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import Card from '@/views/dashboard/_components/card/card.vue'
import StatisticsCards from './_components/statistics-cards.vue'
import DomainBlockingRankingChart from './_components/domain-blocking-ranking-chart.vue'
import TerminalBlockingRankingChart from './_components/terminal-blocking-ranking-chart.vue'
import OnlineClassificationChart from './_components/online-classification-chart.vue'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import DomainInterceptEventList from '../port-manage/_components/domain-intercept-event-list/domain-intercept-event-list.vue'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { TimeViewService } from '../internet-restriction/_components/time-view/time-view.service'
import { TimeViewService as AccessRestrictionTimeViewService } from './service'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import dayjs from 'dayjs'
import PolicyManage from '@/views/dashboard/_components/policy-manage/policy-manage.vue'
import StrategySetting from './components/strategy-setting.vue'
import type { AccessRestrictionStatCardKey } from './_components/access-restriction-statistics-cards.types'
import RankingInterceptDetailDrawers from './_components/ranking-intercept-detail-drawers.vue'
import {
  ACCESS_RESTRICTION_LIMIT_TYPE_KEY as typeKey,
  translateAccessRestrictionLimitType,
} from './access-restriction-limit-type'


export default /*#__PURE__*/_defineComponent({
  __name: 'access-restriction',
  setup(__props) {

const { t } = useI18n()
const injector = useReflectiveInjector([PolicyCommonService, TimeViewService, AccessRestrictionTimeViewService])
const groupPolicySettingsService = injector.get(PolicyCommonService)
const groupRefreshManage = injector.get(GroupRefreshManage)
const servicePermissionService = injector.get(ServicePermissionService)
const accessRestrictionTimeViewService = injector.get(AccessRestrictionTimeViewService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.internet_url_limit)
const saveOrUpdateApi = groupPolicySettingsService.saveSettings.bind(groupPolicySettingsService)
const getApi = groupPolicySettingsService.getSettings.bind(groupPolicySettingsService)

const strategyDrawerVisible = ref(false)
const policyManageRef = ref()
const fromGroupId = ref<number | undefined>(undefined)
const activeRow = ref({} as StrategyListDataCommon)

// --- Statistics Data ---
const statistics = reactive({
  todayBlockedDomains: 0,
  effectivePolicies: 0
})

// --- Chart Data ---
const domainRankingTimeRange = ref<TimeRange>(TimeRange.DAYS_30)
const domainRankingData = ref<Array<{ name: string; count: number }>>([])

const terminalRankingTimeRange = ref<TimeRange>(TimeRange.DAYS_30)
const terminalRankingData = ref<Array<{ name: string; department: string; count: number }>>([])

const classificationTimeRange = ref<TimeRange>(TimeRange.DAYS_30)
const classificationData = ref<Array<{ name: string; percentage: number }>>([])

// --- Log Table ---
const domainInterceptEventListRef = ref()
const domainBlockingRankingChartRef = ref()
const terminalBlockingRankingChartRef = ref()
const onlineClassificationChartRef = ref()
const rankingInterceptDetailDrawersRef = ref<InstanceType<typeof RankingInterceptDetailDrawers> | null>(null)
const logDrawerVisible = ref(false)
const logDrawerKey = ref(0)
const logDrawerEmbed = ref<{ dateRange?: [string, string]; strategyId?: string } | null>(null)
const isShowRawData = ref(false)
const rawDataRow = ref<any>({})

function viewAllLogs() {
  if (!hasPermission) return
  logDrawerEmbed.value = null
  logDrawerKey.value += 1
  logDrawerVisible.value = true
}

function handleAccessRestrictionStatClick(_key: AccessRestrictionStatCardKey) {
  if (statistics.todayBlockedDomains <= 0) return
  const d = dayjs().format('YYYY-MM-DD')
  logDrawerEmbed.value = { dateRange: [d, d] }
  logDrawerKey.value += 1
  logDrawerVisible.value = true
}

function onDomainRankingItemClick (item: { name: string; count: number }) {
  const [start_time, end_time] = getTimeRangeByEnum(domainRankingTimeRange.value)
  rankingInterceptDetailDrawersRef.value?.openDomainFromRanking({
    name: item.name,
    count: item.count,
    start_time: start_time ?? undefined,
    end_time: end_time ?? undefined,
  })
}

function onTerminalRankingItemClick (item: { name: string; count: number; device_id?: string }) {
  rankingInterceptDetailDrawersRef.value?.openTerminalFromRanking(item)
}

const getUrlList = computed(() => item => item?.[typeKey[item.limit_type]]?.split?.(',') || [])

const customCols = computed<TableOptColumn[]>(() => [
  {
    label: t('accessRestriction.policyAttr'),
    prop: 'limit_type',
    formatter: (row) => translateAccessRestrictionLimitType(t, row?.strategy?.limit_type),
  },
  { label: t('accessRestriction.targetDomain'), prop: 'urls', columnOpt: { minWidth: 120 } },
])

function handleStrategyRefresh() {
  policyManageRef.value?.tableRef?.getList()
  groupRefreshManage.refreshGroup()
}

const handleAction = ({ type, ...other }: { type: string;[type: string]: any; }) => {
  const types: Record<string, () => void> = {
    add: () => {
      activeRow.value = {} as StrategyListDataCommon
      fromGroupId.value = policyManageRef.value?.validGroupId
      strategyDrawerVisible.value = true
    },
    edit: () => {
      fromGroupId.value = undefined
      activeRow.value = other.rowItem
      strategyDrawerVisible.value = true
    },
  }
  types[type]?.()
}

/** 策略列表「触发次数」：打开域名拦截事件侧栏，按当前策略 + 近 1 个月查询 */
function handleClickTriggerTime (item: StrategyListDataCommon) {
  const id = item?.id
  if (id == null || id === '') return
  const end = dayjs().format('YYYY-MM-DD')
  const start = dayjs().subtract(1, 'month').format('YYYY-MM-DD')
  logDrawerEmbed.value = {
    dateRange: [start, end],
    strategyId: String(id),
  }
  logDrawerKey.value += 1
  logDrawerVisible.value = true
}

// 加载统计数据
async function loadStatistics() {
  if (!hasPermission) return

  try {
    const now = Math.floor(dayjs().valueOf() / 1000)
    const todayStart = Math.floor(dayjs().startOf('day').valueOf() / 1000)

    const res = await accessRestrictionTimeViewService.getTerminalDomainaccessOverviewStat({
      start_time: todayStart.toString(),
      end_time: now.toString()
    })

    statistics.todayBlockedDomains = res.today_blocked_domain_count || 0
    statistics.effectivePolicies = res.active_policy_count || 0
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载域名排行数据
async function loadDomainRankingData() {
  if (!hasPermission) return

  if (domainBlockingRankingChartRef.value) {
    domainBlockingRankingChartRef.value.loading = true
  }
  try {
    const [start_time, end_time] = getTimeRangeByEnum(domainRankingTimeRange.value)

    const params: any = {
      top_count: 5
    }
    if (start_time !== undefined && end_time !== undefined) {
      params.start_time = start_time.toString()
      params.end_time = end_time.toString()
    }

    const res = await accessRestrictionTimeViewService.getTerminalDomainaccessTopDomainStat(params)

    domainRankingData.value = (res.top_domain_list || []).map(item => ({
      name: item.domain || t('accessRestriction.unknown'),
      count: item.block_count || 0
    }))
  } catch (error) {
    console.error('加载域名排行数据失败:', error)
    domainRankingData.value = []
  } finally {
    if (domainBlockingRankingChartRef.value) {
      domainBlockingRankingChartRef.value.loading = false
    }
  }
}

// 加载终端排行数据
async function loadTerminalRankingData() {
  if (!hasPermission) return

  if (terminalBlockingRankingChartRef.value) {
    terminalBlockingRankingChartRef.value.loading = true
  }
  try {
    const [start_time, end_time] = getTimeRangeByEnum(terminalRankingTimeRange.value)

    const params: any = {
      top_count: 5
    }
    if (start_time !== undefined && end_time !== undefined) {
      params.start_time = start_time.toString()
      params.end_time = end_time.toString()
    }

    const res = await accessRestrictionTimeViewService.getTerminalDomainaccessTopTerminalStat(params)

    terminalRankingData.value = (res.top_terminal_list || []).map(item => ({
      name: item.computer_name || t('accessRestriction.unknown'),
      department: item.department || t('overviewZone.ungrouped'),
      count: item.block_count || 0,
      device_id: item.device_id,
    }))
  } catch (error) {
    console.error('加载终端排行数据失败:', error)
    terminalRankingData.value = []
  } finally {
    if (terminalBlockingRankingChartRef.value) {
      terminalBlockingRankingChartRef.value.loading = false
    }
  }
}

// 监听时间范围变化
watch(domainRankingTimeRange, () => {
  loadDomainRankingData()
})

watch(terminalRankingTimeRange, () => {
  loadTerminalRankingData()
})

watch(classificationTimeRange, () => {
  loadClassificationData()
})

// 加载分类数据
async function loadClassificationData() {
  if (!hasPermission) return

  if (onlineClassificationChartRef.value) {
    onlineClassificationChartRef.value.loading = true
  }
  try {
    const [start_time, end_time] = getTimeRangeByEnum(classificationTimeRange.value)

    if (start_time === undefined || end_time === undefined) {
      classificationData.value = []
      return
    }

    const res = await accessRestrictionTimeViewService.getTerminalDomainaccessTopCategoryStat({
      start_time: start_time.toString(),
      end_time: end_time.toString(),
      top_count: 5
    })

    classificationData.value = (res.top_category_list || []).map(item => ({
      name: item.category_name || t('accessRestriction.unknown'),
      percentage: item.ratio || 0
    }))
  } catch (error) {
    console.error('加载分类数据失败:', error)
    classificationData.value = []
  } finally {
    if (onlineClassificationChartRef.value) {
      onlineClassificationChartRef.value.loading = false
    }
  }
}


onMounted(async () => {
  if (hasPermission) {
    await nextTick()
    loadStatistics()
    loadDomainRankingData()
    loadTerminalRankingData()
    loadClassificationData()
  }
})


return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageSubscribeBar, {
      "normal-permission-key": _unref(PermissionFe).internet_url_limit
    }, null, 8, ["normal-permission-key"]),
    _createVNode(StatisticsCards, {
      "today-blocked-domains": statistics.todayBlockedDomains,
      "effective-policies": statistics.effectivePolicies,
      onStatClick: handleAccessRestrictionStatClick
    }, null, 8, ["today-blocked-domains", "effective-policies"]),
    _createVNode(_component_el_row, { gutter: 16 }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, {
          span: 8,
          style: {"height":"360px"}
        }, {
          default: _withCtx(() => [
            _createVNode(DomainBlockingRankingChart, {
              ref_key: "domainBlockingRankingChartRef",
              ref: domainBlockingRankingChartRef,
              "time-range": domainRankingTimeRange.value,
              "onUpdate:timeRange": _cache[0] || (_cache[0] = ($event: any) => ((domainRankingTimeRange).value = $event)),
              data: domainRankingData.value,
              onItemClick: onDomainRankingItemClick
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 8 }, {
          default: _withCtx(() => [
            _createVNode(TerminalBlockingRankingChart, {
              ref_key: "terminalBlockingRankingChartRef",
              ref: terminalBlockingRankingChartRef,
              "time-range": terminalRankingTimeRange.value,
              "onUpdate:timeRange": _cache[1] || (_cache[1] = ($event: any) => ((terminalRankingTimeRange).value = $event)),
              data: terminalRankingData.value,
              onItemClick: onTerminalRankingItemClick
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 8 }, {
          default: _withCtx(() => [
            _createVNode(OnlineClassificationChart, {
              ref_key: "onlineClassificationChartRef",
              ref: onlineClassificationChartRef,
              "time-range": classificationTimeRange.value,
              "onUpdate:timeRange": _cache[2] || (_cache[2] = ($event: any) => ((classificationTimeRange).value = $event)),
              data: classificationData.value
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('accessRestriction.domainInterceptEventTitle'),
      class: "cm-ui-mt16 cm-ui-mb16",
      onViewMore: viewAllLogs,
      "view-more-disabled": !_unref(hasPermission),
      viewMoreText: _ctx.$t('accessRestriction.viewAll'),
      viewMoreButtonType: ""
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(DomainInterceptEventList, {
          "view-mode": _unref(TableViewMode).preview,
          ref_key: "domainInterceptEventListRef",
          ref: domainInterceptEventListRef
        }, null, 8, ["view-mode"])
      ]),
      _: 1
    }, 8, ["title", "view-more-disabled", "viewMoreText"]),
    _createVNode(PolicyManage, {
      title: _ctx.$t('accessRestriction.policyList'),
      ref_key: "policyManageRef",
      ref: policyManageRef,
      "normal-permission-key": _unref(PermissionFe).internet_url_limit,
      "strategy-type": _unref(PolicyType).internet,
      "data-type": _unref(PolicySettingType).internet,
      "custom-cols": customCols.value,
      "trigger-time-callback": handleClickTriggerTime,
      "show-group-selector": true,
      "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
      onHandleAction: handleAction
    }, {
      table__urls: _withCtx(({ row }: { row: StrategyListDataCommon }) => [
        _createVNode(ItemGroup, {
          data: getUrlList.value(row.strategy)
        }, null, 8, ["data"])
      ]),
      _: 1
    }, 8, ["title", "normal-permission-key", "strategy-type", "data-type", "custom-cols", "terminal-type"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": rawDataRow.value
    }, null, 8, ["modelValue", "raw-data"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('accessRestriction.allDomainInterceptEvents'),
      modelValue: logDrawerVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((logDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(DomainInterceptEventList, {
          key: logDrawerKey.value,
          viewMode: _unref(TableViewMode).full,
          "embed-defaults": logDrawerEmbed.value,
          style: {"padding":"10px 16px"}
        }, null, 8, ["viewMode", "embed-defaults"]))
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('accessRestriction.strategySetting'),
      modelValue: strategyDrawerVisible.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((strategyDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(StrategySetting, {
          modelValue: strategyDrawerVisible.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((strategyDrawerVisible).value = $event)),
          api: { getApi: _unref(getApi), saveOrUpdateApi: _unref(saveOrUpdateApi) },
          id: activeRow.value.id || '',
          "has-permission": _unref(hasPermission),
          "from-group-id": fromGroupId.value,
          onRefresh: handleStrategyRefresh
        }, null, 8, ["modelValue", "api", "id", "has-permission", "from-group-id"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(RankingInterceptDetailDrawers, {
      ref_key: "rankingInterceptDetailDrawersRef",
      ref: rankingInterceptDetailDrawersRef,
      "has-permission": _unref(hasPermission)
    }, null, 8, ["has-permission"])
  ], 64))
}
}

})